import { ISelectionHelperTool, IToolEditor, IToolModel, IToolData, ToolId, SelectionMode, Mask } from '../interfaces';
import { invalidEnum } from '../baseUtils';
import { deserializeMask, invertMask, addRectToMask, clearMask, addPolyToMask, moveMask, serializeMask } from '../mask';
import { logAction } from '../actionLog';
import { redraw, redrawDrawing } from '../../services/editorUtils';
import { createPolyOutlineFromBitmap } from '../polyUtils';
import { finishTransform } from '../toolUtils';

export enum SelectionHelperAction {
  DeselectAll,
  SelectAll,
  InvertSelection,
  FillSelection,
  InitSelection,
  MoveSelection,
}

export interface IData extends IToolData {
  action: SelectionHelperAction;
  selectionData?: number[];
  dx?: number;
  dy?: number;
}

export class SelectionHelperTool implements ISelectionHelperTool {
  id = ToolId.SelectionHelper;
  name = '';
  constructor(public editor: IToolEditor, public model: IToolModel) {
  }
  do(data: IData) {
    switch (data.action) {
      case SelectionHelperAction.DeselectAll: this.deselectAll(true); break;
      case SelectionHelperAction.SelectAll: this.selectAll(true); break;
      case SelectionHelperAction.InvertSelection: this.invertSelection(true); break;
      case SelectionHelperAction.FillSelection: this.fillSelection(true); break;
      case SelectionHelperAction.InitSelection: this.initSelection(data.selectionData, true); break;
      case SelectionHelperAction.MoveSelection: this.moveSelection(data.dx ?? 0, data.dy ?? 0, true); break;
      default: invalidEnum(data.action, `invalid selection helper action`);
    }
  }
  deselectAll(remote = false) {
    logAction(`[${remote ? 'remote' : 'local'}] deselectAll (clientId: ${this.model.user.localId})`);

    finishTransform(this.editor, this.model.user, 'deselectAll');
    this.model.user.history.pushSelection('deselectAll');
    clearMask(this.model.user.selection);
    this.model.doTool<IData>(0, { id: this.id, action: SelectionHelperAction.DeselectAll, selection: SelectionMode.Break });
    redraw(this.editor);
  }
  selectAll(remote = false) {
    logAction(`[${remote ? 'remote' : 'local'}] selectAll (clientId: ${this.model.user.localId})`);

    finishTransform(this.editor, this.model.user, 'selectAll');
    this.model.user.history.pushSelection('selectAll');
    clearMask(this.model.user.selection);
    addRectToMask(this.model.user.selection, this.editor.drawing.rect);
    this.model.doTool<IData>(0, { id: this.id, action: SelectionHelperAction.SelectAll, selection: SelectionMode.Break });
    redraw(this.editor);
  }
  select(selection: Mask, remote = false) {
    const selectionData = serializeMask(selection);
    logAction(`[${remote ? 'remote' : 'local'}] select (clientId: ${this.model.user.localId})`);

    finishTransform(this.editor, this.model.user, 'select');
    this.model.user.history.pushSelection('select');
    this.model.doTool<IData>(0, { id: this.id, action: SelectionHelperAction.InitSelection, selection: SelectionMode.Break, selectionData });
    redraw(this.editor);
  }
  invertSelection(remote = false) {
    logAction(`[${remote ? 'remote' : 'local'}] invertSelection (clientId: ${this.model.user.localId})`);

    finishTransform(this.editor, this.model.user, 'invertSelection');
    this.model.user.history.pushSelection('invertSelection');
    invertMask(this.model.user.selection, this.editor.drawing.rect);
    this.model.doTool<IData>(0, { id: this.id, action: SelectionHelperAction.InvertSelection, selection: SelectionMode.Update });
    redraw(this.editor);
  }
  initSelection(data: number[] | undefined, remote = false) {
    logAction(`[${remote ? 'remote' : 'local'}] initSelection (clientId: ${this.model.user.localId})`);

    finishTransform(this.editor, this.model.user, 'initSelection');
    // only for initializing user selection
    deserializeMask(this.model.user.selection, data);
    redraw(this.editor);
  }
  selectionFromLayer() {
    logAction(`[local] selectionFromLayer (clientId: ${this.model.user.localId})`);

    finishTransform(this.editor, this.model.user, 'selectionFromLayer');

    const user = this.model.user;
    user.history.pushSelection('selectionFromLayer');

    if (!user.activeLayer) throw new Error(`[selectionFromLayer] No activeLayer`);

    // logAction(`[${remote ? 'remote' : 'local'}] invertSelection (clientId: ${this.model.user.localId})`);
    // this.model.doTool<IData>(0, { id: this.id, action: SelectionHelperAction.InvertSelection, selection: SelectionMode.Update });

    // TODO: maybe use raw data here instead, and then shift final selection by layer.rect.x/y
    const bitmap = this.editor.renderer.getLayerImageData(user.activeLayer);
    const bounds = user.activeLayer.rect;

    clearMask(user.selection);
    addPolyToMask(user.selection, createPolyOutlineFromBitmap(bitmap, bounds));
    // TODO: we need to send mask that we generated otherwise we can have discrepancies
    redraw(this.editor);
  }
  moveSelection(dx: number, dy: number, remote = false) {
    logAction(`[${remote ? 'remote' : 'local'}] moveSelection (clientId: ${this.model.user.localId})`);

    finishTransform(this.editor, this.model.user, 'moveSelection');
    this.model.user.history.pushSelection('moveSelection');
    moveMask(this.model.user.selection, dx, dy);
    this.model.doTool<IData>(0, { id: this.id, action: SelectionHelperAction.MoveSelection, selection: SelectionMode.Update, dx, dy });
    redraw(this.editor);
  }
  // helper method for testing selection
  fillSelection(remote = false) {
    const user = this.model.user;
    logAction(`[${remote ? 'remote' : 'local'}] fillSelection (clientId: ${user.localId})`);

    if (!user.activeLayer) throw new Error(`[fillSelection] No activeLayer`);

    finishTransform(this.editor, user, 'fillSelection');
    this.editor.renderer.fillSelection(user.activeLayer);
    this.model.doTool<IData>(user.activeLayer.id, { id: this.id, action: SelectionHelperAction.FillSelection });
    redrawDrawing(this.editor);
  }
}
